import { useRouter } from "next/router";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { IFirstLevelNavItem } from "@/interfaces/INavigationDTO";
import { hoveredElementIdAtom } from "@/atoms/NavigationAtoms";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LaunchCard from "../Card/LaunchCard";
import getPathByLink from "@/utils/getPathByLink";
import { configColor } from "@/utils";

interface IMegaMenuProps {
  navItems: IFirstLevelNavItem[],
  language: string,
  categoryData: any,
  isBoutiqueEnabled: boolean
}

const MegaMenu: React.FC<IMegaMenuProps> = ({ navItems, language, categoryData, isBoutiqueEnabled }) => {

  const { t, i18n } = useTranslation();
  const lang = language || i18n.language;

  const setHoveredElementId = useSetRecoilState(hoveredElementIdAtom)
  const selectedElementId = useRecoilValue(hoveredElementIdAtom);
  const [sideNavHeight, setSideNavHeight] = useState<number>(388);
  const router = useRouter();

  const selectedItem = navItems.find(item => item.metainfo.id === selectedElementId);

  const sideNavItems = useMemo(() => {
    if (!selectedItem?.ti) return null;
    return selectedItem.ti
      .filter(item => item.grpTypeMenu === "grpSecondLevelDirect" && !item.flgHideFromMenu)
      .map(item => (
        <a onClick={() => setHoveredElementId(null)} aria-current={router.pathname === getPathByLink(item, lang) ? "page" : undefined} key={item.metainfo.id} href={getPathByLink(item, lang)}>
          <div className="cursor-pointer font-bold">{item.labelMultilanguage[lang]}</div>
        </a>
      ));
  }, [navItems, selectedElementId, lang]);

  const containerNav = useMemo(() => {
    // Nome nodo vcms 'Categorie'
    if (selectedItem?.label == 'Categorie' && isBoutiqueEnabled) {
        const isMalpensa = process.env.NEXT_PUBLIC_AIRPORT_REFERENCE?.toLowerCase() === 'malpensa';
        const boutiqueSite = isMalpensa
            ? process.env.NEXT_PUBLIC_SITE_BOUTIQUE_MXP
            : process.env.NEXT_PUBLIC_SITE_BOUTIQUE_LIN;
        return (
            <div className="flex flex-col gap-4 items-stretch h-fit">
                  <div className="colTitle text-mediumgrey w-full uppercase">{t('menulabels.labelBoutique')}</div>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4 items-stretch h-fit">
                {categoryData?.map((item: any, index: number) => {
                    return ( 
                        <a 
                            className='cursor-pointer'
                            onClick={() => setHoveredElementId(null)} 
                            target='_blank' 
                            key={index} 
                            href={boutiqueSite + item.custom_url.url}
                        >
                                {item.name}
                        </a>
                    )
                })}
                </div>
            </div>
        );
    }

    if (!selectedItem) return null;
    if (!selectedItem?.ti) return null;

    return selectedItem.ti
      .filter(item => item.grpTypeMenu === 'grpSecondLevelContainer')
      .map(item => (
        <nav key={item.metainfo.id} aria-label={item.labelMultilanguage[lang]} className="flex flex-col gap-4">
          <h2 className="sr-only">{item.labelMultilanguage[lang]}</h2>
          <div className="colTitle text-mediumgrey">{item.labelMultilanguage[lang]}</div>
          {item.ti?.map((subItem: any) => (
            !subItem.flgHideFromMenu && (
              <a key={subItem.metainfo.id} href={getPathByLink(subItem, lang)} onClick={() => setHoveredElementId(null)}>
                <div>{subItem.labelMultilanguage[lang]}</div>
              </a>
            )
          ))}
        </nav>
      ));
  }, [navItems, selectedElementId, lang, categoryData, isBoutiqueEnabled, t, setHoveredElementId]);

  const bannerNavigation = useMemo(() => {
    if (!selectedItem?.ti) return null;
    const imageEl = selectedItem.ti.find(item => item.grpTypeMenu === 'grpSecondLevelBanner');
    if (!imageEl) return null;

    const imageAddr = imageEl.upiImageDesktop?.mediaserverpath && imageEl.upiImageDesktop?.resourcename
      ? `${imageEl.upiImageDesktop.mediaserverpath}/${imageEl.upiImageDesktop.resourcename}`
      : '';

    return getPathByLink(imageEl, lang) ? (
      <a href={getPathByLink(imageEl, lang)} target={imageEl?.flgTarget ? "_blank" : "_self"}>
        <LaunchCard
          isMenu
          txtColor={configColor("text", imageEl.enuBackgroundLabel)}
          isLaunch
          textSize="sm"
          imageUrl={imageAddr}
          title={imageEl?.labelMultilanguage[lang] || ''}
          onClick={() => {}}
          isSquare
        />
      </a>
    ) : null;
  }, [navItems, selectedElementId, lang]);

  return (
    <div
      style={{ transition: 'height .6s', height: selectedElementId && sideNavHeight && selectedElementId !== 999 ? sideNavHeight + 32 : 0 }}
      className={`text-black flex ${selectedElementId && sideNavHeight && selectedElementId !== 999 ? 'py-4 px-10' : ''} justify-between overflow-hidden absolute w-full bg-white z-50`}
    >

    {sideNavItems?.length ? (
      <nav
        id="sideNav"
        className="flex flex-col gap-5 w-auto min-w-[285px] max-h-full h-fit border-r-2 border-r-lightgrey"
        aria-label={`Percorso di navigazione: sezione ${selectedItem?.labelMultilanguage?.[lang]}`}
      >
        <h2 className="sr-only">{`Sei nella sezione: ${selectedItem?.labelMultilanguage?.[lang]}`}</h2>
        {sideNavItems}
      </nav>
    ) : null}


      <div className="flex justify-start flex-auto px-8 gap-14">
        {containerNav}
      </div>

      {bannerNavigation && (
        <div className="aspect-square h-60 overflow-hidden">
          {bannerNavigation}
        </div>
      )}
    </div>
  );
};

export default MegaMenu;
